import { useEffect, useRef, useState } from "react";
import { ASSETS, LINKS } from "@/lib/brand";
import { ArrowUpRight } from "lucide-react";

/**
 * Pinned cinematic scroll-video chapter.
 * - 600vh outer container, inner sticky 100svh panel pins the video
 * - Scroll progress (0..1) → video.currentTime
 * - rAF lerp for smooth scrubbing
 * - 5 text states cross-fade based on progress thresholds
 * - Mobile: same approach; if metadata never resolves, video stays as poster but
 *   text states still progress (graceful)
 * - prefers-reduced-motion: scrubbing is disabled, final state shown immediately
 */

const STATES = [
  {
    label: "AI PRODUCT STUDIO",
    headline: "Enter the AI operating layer.",
    sub: "Premium systems built for modern business execution.",
    range: [0.0, 0.18],
  },
  {
    label: "CONNECTED INFRASTRUCTURE",
    headline: "Every workflow. Connected.",
    sub: "Websites, funnels, voice and automation designed as one system.",
    range: [0.18, 0.38],
  },
  {
    label: "AI SALES SYSTEMS",
    headline: "From attention to action.",
    sub: "Capture demand, qualify leads and automate the next step.",
    range: [0.38, 0.6],
  },
  {
    label: "OPERATIONAL AI",
    headline: "Built to work, not just impress.",
    sub: "Web applications and AI workflows deployed around real business needs.",
    range: [0.6, 0.82],
  },
  {
    label: "FORGELAYER AI",
    headline: "Build the next layer of your business with AI.",
    sub: "Websites, voice agents, automations and software systems — designed to turn operational friction into measurable growth.",
    range: [0.82, 1.0],
    showCta: true,
  },
];

function pickActiveIndex(p) {
  for (let i = 0; i < STATES.length; i++) {
    const [a, b] = STATES[i].range;
    if (p >= a && p < b) return i;
  }
  return STATES.length - 1;
}

export default function Hero() {
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const videoRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const video = videoRef.current;
    if (!wrapper || !video) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let duration = 0;
    let target = 0;
    let current = 0;
    let raf = 0;
    let lastSetTime = -1;
    let cancelled = false;

    const onMeta = () => {
      duration = video.duration || 0;
      if (duration > 0) {
        setVideoReady(true);
        // Make sure we render the first frame
        try {
          video.currentTime = 0.001;
        } catch (e) {}
      }
    };

    const safeSet = (t) => {
      if (
        duration > 0 &&
        Math.abs(t - lastSetTime) > 1 / 60 &&
        video.readyState >= 2
      ) {
        try {
          video.currentTime = Math.min(Math.max(t, 0), duration - 0.05);
          lastSetTime = t;
        } catch (e) {}
      }
    };

    const computeProgress = () => {
      const rect = wrapper.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      return total > 0 ? scrolled / total : 0;
    };

    const updateState = (p) => {
      setActiveIdx(pickActiveIndex(p));
    };

    const onScroll = () => {
      const p = computeProgress();
      target = p * (duration || 1);
      updateState(p);
    };

    const loop = () => {
      if (cancelled) return;
      if (reduced) {
        // No scrubbing in reduced motion mode
      } else {
        // smooth lerp toward scroll-derived target
        current += (target - current) * 0.18;
        safeSet(current);
      }
      raf = requestAnimationFrame(loop);
    };

    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("loadeddata", onMeta);
    if (video.readyState >= 1) onMeta();

    // Trigger metadata load on iOS by briefly playing then pausing (muted+inline)
    const primeIos = async () => {
      try {
        video.muted = true;
        // play returns a promise; ignore failures
        const pl = video.play();
        if (pl && typeof pl.then === "function") {
          await pl.catch(() => {});
        }
        video.pause();
      } catch (e) {
        // ignore — currentTime= will still work after enough buffer is loaded
      }
    };
    primeIos();

    // Initial run
    onScroll();
    loop();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    if (reduced) {
      // jump to final state visually
      const onLoadedForReduced = () => {
        try {
          video.currentTime = (video.duration || 0) - 0.1;
        } catch (e) {}
        setActiveIdx(STATES.length - 1);
      };
      video.addEventListener("loadeddata", onLoadedForReduced, { once: true });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("loadeddata", onMeta);
    };
  }, []);

  return (
    <section
      id="top"
      className="hero-chapter"
      ref={wrapperRef}
      data-testid="hero-chapter"
      aria-label="ForgeLayer AI cinematic introduction"
    >
      <div className="hero-sticky" ref={stickyRef} data-testid="hero-sticky">
        <video
          ref={videoRef}
          className="hero-video"
          muted
          playsInline
          preload="auto"
          // eslint-disable-next-line react/no-unknown-property
          disablePictureInPicture
          // eslint-disable-next-line react/no-unknown-property
          x-webkit-airplay="deny"
          aria-hidden="true"
          data-testid="hero-video"
        >
          {/* Browsers will pick the first source they can decode.
              WebM/VP9 first ensures open-source Chromium (and headless test
              environments) can play. Real browsers + Safari fall back to MP4/H.264. */}
          <source src={ASSETS.heroVideoWebm} type="video/webm" />
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
        <div className="hero-vignette" aria-hidden="true" />
        <div className="hero-noise" aria-hidden="true" />

        {/* Right-side rail */}
        <aside className="hero-rail" aria-hidden="true" data-testid="hero-rail">
          <div className="rail-block">
            <div className="rail-title">Capabilities</div>
            <ul>
              <li>AI Websites</li>
              <li>Voice Agents</li>
              <li>Web Applications</li>
              <li>SEO / GEO</li>
              <li>Automation Systems</li>
            </ul>
          </div>
          <div className="rail-block">
            <div className="rail-title">Selected Systems</div>
            <ul>
              <li>Healthcare AI</li>
              <li>Assessment Funnels</li>
              <li>Voice Workflows</li>
              <li>Vertical SaaS</li>
            </ul>
          </div>
        </aside>

        {/* Synchronized state overlays */}
        <div className="state-overlay">
          {STATES.map((s, i) => (
            <div
              key={i}
              className={`state-card${i === activeIdx ? " is-active" : ""}`}
              data-testid={`hero-state-${i}`}
            >
              <div className="eyebrow">{s.label}</div>
              <h1>{s.headline}</h1>
              <p>{s.sub}</p>
              {s.showCta ? (
                <>
                  <div className="state-cta-row">
                    <a
                      href={LINKS.getAudit}
                      className="btn-primary"
                      data-testid="hero-cta-get-audit"
                    >
                      Get My Free AI Audit
                      <span className="btn-arrow" aria-hidden="true">
                        <ArrowUpRight size={13} strokeWidth={2.2} />
                      </span>
                    </a>
                    <a
                      href={LINKS.seeBuild}
                      className="btn-ghost"
                      data-testid="hero-cta-see-build"
                    >
                      See What We Build
                    </a>
                  </div>
                  <p
                    className="state-microcopy"
                    data-testid="hero-microcopy"
                  >
                    Start with your website. Discover what to improve, automate or build next.
                  </p>
                </>
              ) : null}
            </div>
          ))}
        </div>

        {/* Scroll cue (hidden once at final state) */}
        <div
          className="scroll-cue"
          style={{
            opacity: activeIdx >= STATES.length - 1 ? 0 : 1,
            transition: "opacity 600ms ease",
          }}
          data-testid="scroll-cue"
        >
          <span>Scroll to explore</span>
          <span className="cue-line" />
        </div>

        {/* Subtle loading state (no spinner) */}
        {!videoReady ? (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(60% 50% at 50% 50%, rgba(30,144,255,0.12), transparent 70%)",
              pointerEvents: "none",
            }}
          />
        ) : null}
      </div>
    </section>
  );
}
